import { ReasonSuggestions } from '@modules/feedback/components/FeedbackDetails/utils/getReasonSuggestionsFromSuggestedReasons';
import {
  useIsAddPredictionSearchV2Enabled,
  useIsFeedbackOnFeedbackEnabled,
} from '@modules/feedback/hooks';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Badge,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import { Feedback, FeedbackContentType } from 'v2/core/entities/feedback';

interface FeedbackPredictionActionsProps {
  feedback: Feedback;
  reasonSuggestions: ReasonSuggestions[];
  areSuggestionsLoading: boolean;
  isAddPredictionsVisible: boolean;
  shouldShowEditPredictionsButton?: boolean;
  isDetailedView?: boolean;
  onAddPredictionIconClick: () => void;
  onClose: () => void;
}

export const FeedbackPredictionActions = ({
  feedback,
  reasonSuggestions,
  areSuggestionsLoading,
  isAddPredictionsVisible,
  shouldShowEditPredictionsButton = false,
  isDetailedView = false,
  onAddPredictionIconClick,
  onClose,
}: FeedbackPredictionActionsProps) => {
  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();
  const isAddPredictionSearchV2Enabled = useIsAddPredictionSearchV2Enabled();

  const isAddingPredictionsDisabled =
    feedback.content.type === FeedbackContentType.Audio;

  if (
    !isFeedbackOnFeedbackEnabled &&
    (!isDetailedView || !shouldShowEditPredictionsButton)
  ) {
    return null;
  }

  if (isAddPredictionsVisible) {
    return (
      <IconButton
        color="primary"
        onClick={onClose}
        sx={{ p: isAddPredictionSearchV2Enabled ? 0 : undefined }}
      >
        <CloseRoundedIcon />
      </IconButton>
    );
  }

  if (isAddingPredictionsDisabled) {
    return (
      <Tooltip
        arrow
        placement={isDetailedView ? 'left' : 'right'}
        title={`Adding Predictions is not available for ${feedback.sourceV2?.name}`}
      >
        <AddCircleOutlineRoundedIcon color="disabled" />
      </Tooltip>
    );
  }

  const badgeLoader = (
    <Stack justifyContent="center" alignItems="center" width="100%">
      <CircularProgress
        sx={{
          width: '100%',
          color: 'background.default',
        }}
        size="small"
      />
    </Stack>
  );

  const shouldShowOrangeDot = feedback.hasUnpredictedRCs && !isDetailedView;

  const shouldShowPrimaryBadge =
    !shouldShowOrangeDot &&
    (areSuggestionsLoading || reasonSuggestions.length === 0);

  const renderBadgeContent = () => {
    if (shouldShowOrangeDot) {
      return null;
    }
    return areSuggestionsLoading ? badgeLoader : reasonSuggestions.length;
  };

  return (
    <IconButton
      color="primary"
      onClick={onAddPredictionIconClick}
      sx={{ p: isAddPredictionSearchV2Enabled ? 0 : undefined }}
    >
      <Badge
        badgeContent={renderBadgeContent()}
        variant={shouldShowOrangeDot ? 'dot' : 'standard'}
        color={shouldShowPrimaryBadge ? 'primary' : 'warning'}
        sx={{
          '&.MuiBadge-badge': {
            px: '4px !important',
          },
        }}
      >
        <AddCircleOutlineRoundedIcon />
      </Badge>
    </IconButton>
  );
};
